import React from 'react';
import './Terms.css';

const Terms: React.FC = () => {
  return (
    <div className="terms-page">
      <div className="terms-container">
        <h1 className="terms-title">Terms of Service</h1>
        <p className="terms-updated">Last Updated: April 21, 2026</p>

        <section className="terms-section">
          <h2>Agreement to Terms</h2>
          <p>
            ChowMeDown is operated by <strong>ChowMeDown LLC</strong>, a limited liability company registered 
            in Wyoming, United States, and operated from France.
          </p>
          <p>
            By accessing and using ChowMeDown ("the Service"), you agree to be bound by these Terms of Service. 
            If you do not agree to these terms, please do not use our Service. We reserve the right to modify 
            these terms at any time, and your continued use of the Service constitutes acceptance of any changes.
          </p>
        </section>

        <section className="terms-section">
          <h2>Use at Your Own Risk</h2>
          <div className="warning-box">
            <p>
              <strong>Important:</strong> ChowMeDown provides nutritional information and recipe suggestions for 
              informational purposes only. You use this Service entirely at your own risk. We do not guarantee 
              the accuracy, completeness, or suitability of any recipes or nutritional information for your 
              specific dog's needs.
            </p>
          </div>
          <p>
            Every dog is unique, and nutritional requirements vary based on age, breed, health conditions, 
            activity level, and other factors. You are solely responsible for:
          </p>
          <ul>
            <li>Evaluating the suitability of any recipe or feeding recommendation for your dog</li>
            <li>Monitoring your dog's health and response to dietary changes</li>
            <li>Making informed decisions about your dog's nutrition and care</li>
            <li>Any consequences resulting from using recipes or information from our Service</li>
          </ul>
          <p>
            We do not guarantee any specific results, outcomes, or improvements to your dog's health from using the Service.
          </p>
        </section>

        <section className="terms-section">
          <h2>Not Veterinary Advice</h2>
          <div className="warning-box">
            <p>
              <strong>Disclaimer:</strong> ChowMeDown does NOT provide veterinary or medical advice. The 
              information, recipes, and guidance provided through our Service are for general educational 
              purposes only and should not replace professional veterinary consultation.
            </p>
          </div>
          <p>
            <strong>Always consult with a licensed veterinarian before:</strong>
          </p>
          <ul>
            <li>Making significant changes to your dog's diet</li>
            <li>Transitioning to homemade meals</li>
            <li>Feeding a dog with health conditions, allergies, or special dietary needs</li>
            <li>Determining appropriate portion sizes and feeding schedules</li>
            <li>Addressing any health concerns or symptoms</li>
          </ul>
          <p>
            If your dog shows signs of illness, distress, or adverse reactions to any food, seek immediate 
            veterinary care. Do not rely solely on information from ChowMeDown for medical decisions.
          </p>
        </section>

        <section className="terms-section">
          <h2>Premium Subscription Terms</h2>
          
          <h3>Subscription Plans</h3>
          <p>
            ChowMeDown offers both free and premium subscription tiers. Premium features may include:
          </p>
          <ul>
            <li>Unlimited access to all recipes</li>
            <li>Custom recipe generation</li>
            <li>Multiple dog profiles</li>
            <li>Advanced nutritional tools</li>
            <li>Priority support</li>
          </ul>

          <h3>Billing and Payment</h3>
          <ul>
            <li><strong>Recurring Charges:</strong> Premium subscriptions are billed automatically on a recurring basis (monthly or annually, depending on your plan)</li>
            <li><strong>Payment Method:</strong> You authorize us to charge your payment method on file for subscription fees</li>
            <li><strong>Price Changes:</strong> We reserve the right to change subscription prices with at least 30 days' notice</li>
            <li><strong>Failed Payments:</strong> If payment fails, we may suspend or cancel your subscription</li>
            <li><strong>No Refunds:</strong> All subscription payments are final and non-refundable except where required by law. We do not provide refunds for unused time or partial billing periods.</li>
          </ul>

          <h3>Cancellation Policy</h3>
          <p>
            <strong>You can cancel your subscription at any time.</strong> To cancel:
          </p>
          <ol>
            <li>Log in to your account</li>
            <li>Navigate to Settings</li>
            <li>Select "Manage Subscription"</li>
            <li>Click "Cancel Subscription"</li>
          </ol>
          <p>
            Upon cancellation, you will retain access to premium features until the end of your current 
            billing period. No refunds will be provided for partial billing periods. After cancellation, 
            your account will revert to the free tier.
          </p>

          <h3>Free Trial</h3>
          <p>
            We may offer free trials for premium features. If you do not cancel before the trial ends, 
            you will be automatically charged for a paid subscription. Trial terms will be clearly 
            communicated at sign-up.
          </p>
        </section>

        <section className="terms-section">
          <h2>Account Rules and Responsibilities</h2>
          
          <h3>Age Requirement</h3>
          <p>
            You must be at least 18 years old to use this Service or have permission from a parent or legal guardian.
          </p>

          <h3>Account Creation</h3>
          <p>To use certain features, you must create an account. You agree to:</p>
          <ul>
            <li>Provide accurate, current, and complete information</li>
            <li>Maintain the security of your password</li>
            <li>Notify us immediately of any unauthorized use of your account</li>
            <li>Be responsible for all activity under your account</li>
            <li>Not share your account credentials with others</li>
          </ul>

          <h3>Prohibited Activities</h3>
          <p>You may not:</p>
          <ul>
            <li>Use the Service for any illegal purpose</li>
            <li>Violate any laws or regulations</li>
            <li>Impersonate others or provide false information</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Attempt to gain unauthorized access to any portion of the Service</li>
            <li>Use automated systems (bots, scrapers) to access the Service</li>
            <li>Copy, reproduce, or redistribute our content without permission</li>
            <li>Reverse engineer or attempt to extract source code</li>
            <li>Upload viruses, malware, or malicious code</li>
          </ul>

          <h3>Account Termination</h3>
          <p>
            We reserve the right to suspend or terminate your account at any time, without notice, 
            for violation of these Terms, fraudulent activity, or any conduct we deem harmful to 
            the Service or other users.
          </p>
        </section>

        <section className="terms-section">
          <h2>Limitation of Liability</h2>
          <div className="warning-box">
            <p>
              <strong>To the fullest extent permitted by law, ChowMeDown, its owners, employees, and 
              affiliates shall not be liable for any indirect, incidental, special, consequential, or 
              punitive damages, including but not limited to:</strong>
            </p>
          </div>
          <ul>
            <li>Injury, illness, or death of any animal resulting from use of our recipes or information</li>
            <li>Veterinary bills or medical expenses</li>
            <li>Loss of data or profits</li>
            <li>Service interruptions or technical issues</li>
            <li>Errors or inaccuracies in content</li>
            <li>Unauthorized access to your account</li>
          </ul>
          <p>
            Our total liability for any claim arising from your use of the Service shall not exceed 
            the amount you paid to ChowMeDown in the 12 months preceding the claim, or $100, whichever 
            is less.
          </p>
          <p>
            Some jurisdictions do not allow the exclusion of certain warranties or limitation of 
            liability, so these limitations may not apply to you.
          </p>
          <p>
            The Service is provided "as is" and "as available" without warranties of any kind, whether express or implied.
          </p>
        </section>

        <section className="terms-section">
          <h2>Intellectual Property</h2>
          <p>
            All content on ChowMeDown, including recipes, text, graphics, logos, and software, is the 
            property of ChowMeDown or its licensors and is protected by copyright and intellectual 
            property laws. You may not:
          </p>
          <ul>
            <li>Reproduce, distribute, or create derivative works without permission</li>
            <li>Remove copyright or proprietary notices</li>
            <li>Use our content for commercial purposes without authorization</li>
          </ul>
          <p>
            You retain ownership of any content you submit (such as dog profiles), but grant us a 
            license to use it to provide and improve the Service.
          </p>
        </section>

        <section className="terms-section">
          <h2>Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless ChowMeDown, its owners, employees, and affiliates 
            from any claims, damages, losses, liabilities, and expenses (including legal fees) arising 
            from your use of the Service, violation of these Terms, or infringement of any third-party rights.
          </p>
        </section>

        <section className="terms-section">
          <h2>Third-Party Services</h2>
          <p>
            Our Service may contain links to third-party websites or integrate with third-party services 
            (such as payment processors). We are not responsible for the content, privacy practices, or 
            terms of service of any third-party sites or services.
          </p>
        </section>

        <section className="terms-section">
          <h2>Modifications to the Service</h2>
          <p>
            We reserve the right to modify, suspend, or discontinue any part of the Service at any time, 
            with or without notice. We are not liable for any modification, suspension, or discontinuation 
            of the Service.
          </p>
          <p>
            We do not guarantee that the Service will be uninterrupted, secure, or error-free.
          </p>
        </section>

        <section className="terms-section">
          <h2>Governing Law and Disputes</h2>
          <p>
            These Terms are governed by the laws of the State of Wyoming, United States, without regard to 
            conflict of law principles. Any disputes arising from these Terms will be resolved in accordance 
            with applicable laws. Nothing in these Terms limits your rights under applicable consumer protection laws.
          </p>
        </section>

        <section className="terms-section">
          <h2>Severability</h2>
          <p>
            If any provision of these Terms is found to be invalid or unenforceable, the remaining 
            provisions shall continue in full force and effect.
          </p>
        </section>

        <section className="terms-section">
          <h2>Contact Information</h2>
          <p>
            If you have questions about these Terms of Service, please contact us:
          </p>
          <div className="contact-info">
            <p><strong>Email:</strong> <a href="mailto:chowmedown321@gmail.com">chowmedown321@gmail.com</a></p>
            <p><strong>Instagram:</strong> <a href="https://instagram.com/chowmedown_" target="_blank" rel="noopener noreferrer">@chowmedown_</a></p>
            <p><strong>Location:</strong> France</p>
          </div>
        </section>

        <section className="terms-section">
          <p className="acknowledgment">
            <strong>By using ChowMeDown, you acknowledge that you have read, understood, and agree to 
            be bound by these Terms of Service.</strong>
          </p>
        </section>
      </div>
    </div>
  );
};

export default Terms;
